import { CALL_API, CHAIN_API } from '../../middleware/api';

export const START_EDIT_CONTACT_EVENT = Symbol('START_EDIT_CONTACT_EVENT');
export const CHANGE_CONTACT_EVENT = Symbol('CHANGE_CONTACT_EVENT');
export const SAVE_CONTACT_EVENT = Symbol('SAVE_CONTACT_EVENT');
export const SAVE_CONTACT_EVENT_ERROR = Symbol('SAVE_CONTACT_EVENT_ERROR');
export const DELETE_CONTACT_EVENT = Symbol('DELETE_CONTACT_EVENT');
export const ENTER_CONTACT_EMAIL = Symbol('ENTER_CONTACT_EMAIL');
export const ENTER_CONTACT_DEAL_NOTE = Symbol('ENTER_CONTACT_DEAL_NOTE');
export const LEAVE_CONTACT_EMAIL = Symbol('LEAVE_CONTACT_EMAIL');
export const LEAVE_CONTACT_DEAL_NOTE = Symbol('LEAVE_CONTACT_DEAL_NOTE');
export const ENTER_CONTACT_EVENT = Symbol('ENTER_CONTACT_EVENT');
export const LEAVE_CONTACT_EVENT = Symbol('LEAVE_CONTACT_EVENT');
export const FETCHING_EVENT = Symbol('FETCHING_EVENT');
export const FETCHING_EVENT_SUCCESS = Symbol('FETCHING_EVENT_SUCCESS');
export const FETCHING_EVENT_FAILURE = Symbol('FETCHING_EVENT_FAILURE');
export const LOADED_EVENT_LIST = Symbol('LOADED_EVENT_LIST');

export const LOADED_USERS = Symbol('LOADED_USERS');
export const LOADED_DIRECTORS = Symbol('LOADED_DIRECTORS');
export const LOADED_ANALYSTS = Symbol('LOADED_ANALYSTS');

export const CLOSE_VALIDATION_ERROR = Symbol('CLOSE_VALIDATION_ERROR');
export const RESET_CURRENT_EVENT = Symbol('RESET_CURRENT_EVENT');

export function resetCurrentEvent() {
  return {
    type: RESET_CURRENT_EVENT,
  };
}

/**
 * Spawned on mouse entering to deal note.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function enterContactDealNote({ eventId }) {
  return {
    type: ENTER_CONTACT_DEAL_NOTE,
    eventId,
  };
}

/**
 * Spawned on mouse leave deal note.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function leaveContactDealNote({ eventId }) {
  return {
    type: LEAVE_CONTACT_DEAL_NOTE,
    eventId,
  };
}

/**
 * Spawned on mouse entering to event.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function enterContactEvent({ eventId, ...rest }) {
  return {
    ...rest,
    type: ENTER_CONTACT_EVENT,
    eventId,
  };
}

/**
 * Spawned on mouse leave event.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function leaveContactEvent({ eventId }) {
  return {
    type: LEAVE_CONTACT_EVENT,
    eventId,
  };
}

/**
 * Spawned on mouse entering to email.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function enterContactEmail({ eventId }) {
  return {
    type: ENTER_CONTACT_EMAIL,
    eventId,
  };
}

/**
 * Spawned on mouse leave email.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function leaveContactEmail({ eventId }) {
  return {
    type: LEAVE_CONTACT_EMAIL,
    eventId,
  };
}

/**
 * Starts editing of contact event.
 *
 * @param eventId {Number}.
 * @returns {{type: symbol, eventId: number}}
 */
export function startEditContactEvent({ eventId, approach, afterSuccess, ...rest }) {
  if (eventId) {
    return {
      [CALL_API]: {
        method: 'get',
        path: `/api/v1/events/${eventId}`,
        startType: FETCHING_EVENT,
        errorType: FETCHING_EVENT_FAILURE,
        successType: FETCHING_EVENT_SUCCESS,
        afterSuccess,
      },
      eventId,
    };
  }

  return {
    ...rest,
    type: START_EDIT_CONTACT_EVENT,
    eventId,
    approach,
  };
}

/**
 * Change value in contact event.
 *
 * @param field {String} Name of field to change.
 * @param value {String|Number} Value from input.
 * @returns {{type: symbol, field: string, value: string|number}}
 */
export function changeContactEvent({ field, value }) {
  return {
    type: CHANGE_CONTACT_EVENT,
    field,
    value,
  };
}

/**
 * Save contact event.
 */
export function saveContactEvent({ entityId, body, afterSuccess }) {
  const { id } = body;
  let method = 'put';
  let path = `/api/v1/events/${id}`;

  if (!id) {
    method = 'post';
    path = '/api/v1/events';
  }

  return {
    [CHAIN_API]: [
      () => ({
        isNew: !id,
        [CALL_API]: {
          method,
          path,
          body,
          startType: SAVE_CONTACT_EVENT,
          errorType: SAVE_CONTACT_EVENT_ERROR,
          skipGlobalErrorHandler: true,
        },
      }),
      () => ({
        [CALL_API]: {
          method: 'get',
          path: `/api/v1/people/${entityId}?include=events`,
          successType: LOADED_EVENT_LIST,
          afterSuccess,
        },
      }),
    ],
  };
}

/**
 * Remove event.
 *
 * @param id {Number} Event id.
 * @param contactId {Number} Contact id.
 * @param afterSuccess {Function} After success callback.
 * @returns {MiddlewareApi.CallApi}
 */
export function deleteContactEvent({ id, contactId, afterSuccess }) {
  return {
    [CALL_API]: {
      method: 'delete',
      path: `/api/v1/events/${id}`,
      successType: DELETE_CONTACT_EVENT,
      successParams: { eventId: id },
      afterSuccess,
      afterError: ({ dispatch, ...r }, ...rest) => {
        afterSuccess({ dispatch, ...r }, ...rest);
        dispatch(reloadEvents({ entityId: contactId }));
      },
    },
  };
}

/**
 * Load users by filter.
 *
 * @param filter {String}.
 * @param page {Number}.
 * @param afterSuccess {Function}.
 * @returns {MiddlewareApi.CallApi}
 */
export function findUsers({ filter, page, afterSuccess }) {
  return {
    filter,
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/browse/users',
      unifier: params => `${params.path}`,
      maxCount: 2,
      query: {
        like: filter,
        page,
      },
      successType: LOADED_USERS,
      afterSuccess,
    },
  };
}

/**
 * Load directors by filter.
 *
 * @param filter {String}.
 * @param page {Number}.
 * @param afterSuccess {Function}.
 */
export function findDirectors({ filter, page, afterSuccess }) {
  return {
    filter,
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/browse/users',
      unifier: params => `${params.path}`,
      maxCount: 2,
      query: {
        like: filter,
        page,
      },
      successType: LOADED_DIRECTORS,
      afterSuccess,
    },
  };
}

/**
 * Load analysts by filter.
 *
 * @param filter {String}.
 * @param page {Number}.
 * @param afterSuccess {Function}.
 * @returns {MiddlewareApi.CallApi}
 */
export function findAnalysts({ filter, page, afterSuccess }) {
  return {
    filter,
    [CALL_API]: {
      method: 'get',
      path: '/api/v1/browse/users',
      unifier: params => `${params.path}`,
      maxCount: 2,
      query: {
        like: filter,
        page,
      },
      successType: LOADED_ANALYSTS,
      afterSuccess,
    },
  };
}

export function reloadEvents({ entityId }) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/people/${entityId}?include=events`,
      successType: LOADED_EVENT_LIST,
      maxCount: 2,
    },
  };
}

export function closeValidationError({ field }) {
  return {
    type: CLOSE_VALIDATION_ERROR,
    field,
  };
}
